<template>
  <jr-button @click="dialogVisible = true" type="success" v-if="isEdit && hasTemplate" class="template-dialog open-btn">
    テンプレート呼び出し
  </jr-button>
  <jr-dialog title="テンプレート呼び出し" v-model="dialogVisible" width="740px">
    <el-form ref="formRef" :model="form">
      <el-row>
        <el-col :span="5" class="error-message-padding align-middle">
          <input-label :required="true">テンプレート選択</input-label>
        </el-col>
        <el-col :span="19">
          <el-form-item class="template_id" prop="template_id" :rules="radioRule">
            <jr-radio-group
              v-model="form.template_id"
              radioClass="template_id-radio"
              :labels="templateLabels"
              :vertical="true"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="5">
          <input-label>プレビュー</input-label>
        </el-col>
        <el-col :span="19">
          <jr-textarea v-model="selectTemplateBody" :readonly="true" :rows="20" />
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-row>
        <el-col :span="12" class="align-left">
          <jr-button @click="dialogVisible = false" type="info" class="close-btn">閉じる</jr-button>
        </el-col>
        <el-col :span="12" class="align-right">
          <jr-button type="primary" @click="decide" class="decide-btn">決定</jr-button>
        </el-col>
      </el-row>
    </template>
  </jr-dialog>
</template>

<script lang="ts">
import { defineComponent, computed, watch, ref, reactive } from 'vue'
import { AxiosResponse } from 'axios'
import { ElForm } from 'element-plus'

import JrDialog from '@/components/jr-parts/JrDialog.vue'
import JrButton from '@/components/jr-parts/JrButton.vue'
import InputLabel from '@/components/inputs/InputLabel.vue'
import JrRadioGroup from '@/components/jr-parts/JrRadioGroup.vue'
import JrTextarea from '@/components/jr-parts/JrTextarea.vue'

import { addMessageQueue, MESSAGE_TYPE_INFO } from '@/plugins/messageQueue'
import Message from '@/services/message'
import Rule from '@/services/rule'
import { ListRequest, PreviewRequest } from '@/requests/messageTemplate'
import { MessageTemplate } from '@/api'
import MessageTypeLogic from '@/services/messageTypeLogic'
import Logger from '@/services/logger'

interface Form {
  template_id: null | number
}

/**
 * テンプレート選択ダイアログ
 */
export default defineComponent({
  name: 'TemplateSelectDialog',
  emits: ['update:template'],
  components: {
    JrDialog,
    JrButton,
    InputLabel,
    JrRadioGroup,
    JrTextarea,
  },
  props: {
    // メッセージ種別
    messageType: {
      type: Number,
      required: true,
    },
    // 申請基本ID
    requestHeaderId: {
      type: Number,
      required: true,
    },
    // 編集可能か
    isEdit: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, context) {
    // ダイアログ開閉
    const dialogVisible = ref<boolean>(false)
    const form = reactive<Form>({
      template_id: null,
    })
    const formRef = ref<InstanceType<typeof ElForm>>()
    const radioRule = new Rule('テンプレート').requiredSelectNumber().toRule()

    // テンプレートが存在するか
    const hasTemplate = computed(() => {
      const logic = new MessageTypeLogic(props.messageType, props.requestHeaderId)
      return logic.hasTemplate()
    })
    // テンプレート一覧
    const templates = reactive<MessageTemplate[]>([])
    // テンプレート選択
    const templateLabels = computed(() => {
      return templates.map((template) => {
        return {
          value: template.message_template_id,
          text: template.message_subject,
        }
      })
    })
    // 選択しているテンプレートの内容を返す
    const selectTemplateBody = computed(() => {
      const selected = templates.find((template) => {
        return form.template_id === template.message_template_id
      })
      return selected != null ? selected.message_body : ''
    })

    // ダイアログの開閉状況をwatchする
    watch(dialogVisible, async (newVisible, oldVisible) => {
      if (newVisible && !oldVisible) {
        // ダイアログが開いた際、メッセージ種別からテンプレートを取得する
        const request = new ListRequest(props.messageType)
        await request.exec()
        if (!request.success) {
          // 取得失敗時はダイアログを閉じる
          dialogVisible.value = false
          return
        }
        const { data } = request.getResponse() as AxiosResponse<MessageTemplate[]>
        if (data.length > 0) {
          // テンプレートが有る場合、追加
          templates.push(...data)
        } else {
          // テンプレートが無い場合、メッセージを表示し画面を閉じる
          addMessageQueue(MESSAGE_TYPE_INFO, Message.codes.NOT_REGISTER_TEMPLATE)
          dialogVisible.value = false
          // ログ出力
          Logger.warn(`メッセージテンプレートがありません。メッセージ種別[${props.messageType}]`)
        }

        // 存在しない場合は閉じる
      } else if (!newVisible && oldVisible) {
        // ダイアログを閉じた際、テンプレート情報をリセットする
        templates.splice(0)
        form.template_id = null
      }
    })

    const decide = async () => {
      if (!formRef.value) {
        // DOM未生成時は何もしない
        return
      }
      formRef.value.validate(async (valid) => {
        if (valid && form.template_id != null) {
          const request = new PreviewRequest(form.template_id, props.requestHeaderId)
          await request.exec()
          if (request.success) {
            // データ取得成功時はそれをemitする
            const { data } = request.getResponse() as AxiosResponse<MessageTemplate>
            context.emit('update:template', data.message_body)
          }
          dialogVisible.value = false
        }
      })
    }

    return {
      dialogVisible,
      form,
      formRef,
      radioRule,
      hasTemplate,
      templateLabels,
      selectTemplateBody,
      decide,
    }
  },
})
</script>
